import React, { useEffect, useMemo, useContext } from "react";
import styled from "styled-components";
import gsap from "gsap/all";
import { ScreenContext } from "components/Providers";

import colors from "styles/colors";
import text from "styles/text";
import media from "styles/media";

import HeadingAnimation from "components/HeadingAnimation";
import { useMedia } from "utils/hooks";

import BackgroundWEBP from "assets/images/background.webp";
import { zeroPad } from "utils/functions";

const Reveal: React.FC = () => {
  const screen = useContext(ScreenContext);

  const card0Transform = useMedia("rotate(90deg)", "rotate(90deg)", "rotate(0deg)", "rotate(0deg)");
  const card0Left = useMedia("-3.47vw", "-3.47vw", "0vw", "0");
  const card0Top = useMedia("3.47vw", "3.47vw", "0vw", "0");
  const animationTopPerc = useMedia(50.5, 50.5, 50.5, 63);
  const animationBottomPerc = useMedia(45, 45, 45, 39.5);
  const cardHeight = useMedia("23.89vw", "23.89vw", "23.89vw", "91.733vw");
  const cardWidth = useMedia("31.25vw", "31.25vw", "31.25vw", "122vw");
  const gsapStart = useMedia("top 53%", "top 53%", "top 53%", "top 65%");
  const gsapEnd = useMedia("bottom bottom-=75%", "bottom bottom-=75%", "bottom bottom-=85%", "bottom 35%");

  const cards = useMemo(
    () => [
      {
        title: "CREATOR TIPPING",
        text: "Fans can tip their favorite creators with $FCD in the FreshCut app, and creators keep 90% of all FreshCut Diamonds tipped.",
        img: "",
        style: `
        background-position: 50%;
        transform: ${card0Transform};
        height: ${cardHeight};
        width: ${cardWidth};
        left: ${card0Left};
        top: ${card0Top};
      `,
      },
      {
        title: "MEMBERSHIP",
        text: "$FCD holders are granted exclusive membership benefits including FreshCut merch, VIP access to events, giveaways, and much more.",
        img: "",
        style: ` 
        background-position: 50%;
        transform: rotate(180deg);  
        width: 23.89vw;
        height: 31.25vw; 
        left: 0; 
        top: 0;
      `,
      },
      {
        title: "FRESHCUT DAO",
        text: "Members receive voting privileges through the FreshCut DAO, spearheading the platform’s shift towards community-involved governance.",
        img: "",
        style: ` 
        background-position: 50%;
        width: 23.89vw;
        height: 31.25vw;
        left: 0; 
        top: 0;
      `,
      },
    ],
    [card0Left, card0Top, card0Transform, cardHeight, cardWidth]
  );

  useEffect(() => {
    if (!screen.fullWidth && !screen.desktop) {
      const tl = gsap.timeline({
        scrollTrigger: {
          scroller: ".smooth-scroll",
          trigger: "#card_0",
          scrub: true,
          start: gsapStart,
          end: gsapEnd,
        },
      });

      tl.to(
        `#card_inner0`,
        {
          opacity: 1,
          scale: screen.mobile ? 1.1 : 1,
          duration: 1,
        },
        0
      )
        .to(
          `#card_inner0`,
          {
            opacity: 0,
            scale: 1,
            duration: 1,
          },
          1
        )
        .to(
          `#card_bg0`,
          {
            scale: screen.mobile ? 1.1 : 1,
            duration: 1,
          },
          0
        )
        .to(
          `#card_bg0`,
          {
            scale: 1,
            duration: 1,
          },
          1
        );

      const tl2 = gsap.timeline({
        scrollTrigger: {
          scroller: ".smooth-scroll",
          trigger: "#card_1",
          scrub: true,
          start: gsapStart,
          end: gsapEnd,
        },
      });

      tl2
        .to(
          `#card_inner1`,
          {
            opacity: 1,
            scale: screen.mobile ? 1.1 : 1,
            duration: 1,
          },
          0
        )
        .to(
          `#card_inner1`,
          {
            opacity: 0,
            scale: 1,
            duration: 1,
          },
          1
        )
        .to(
          `#card_bg1`,
          {
            scale: screen.mobile ? 1.1 : 1,
            duration: 1,
          },
          0
        )
        .to(
          `#card_bg1`,
          {
            scale: 1,
            duration: 1,
          },
          1
        );

      const tl3 = gsap.timeline({
        scrollTrigger: {
          scroller: ".smooth-scroll",
          trigger: "#card_2",
          scrub: true,
          start: gsapStart,
          end: gsapEnd,
        },
      });

      tl3
        .to(
          `#card_inner2`,
          {
            opacity: 1,
            scale: screen.mobile ? 1.1 : 1,
            duration: 1,
          },
          0
        )
        .to(
          `#card_inner2`,
          {
            opacity: 0,
            scale: 1,
            duration: 1,
          },
          1
        )
        .to(
          `#card_bg2`,
          {
            scale: screen.mobile ? 1.1 : 1,
            duration: 1,
          },
          0
        )
        .to(
          `#card_bg2`,
          {
            scale: 1,
            duration: 1,
          },
          1
        );

      if (screen.mobile) {
        tl.to(`#card_bg2`, {
          scale: 1,
        });
      }

      return () => {
        tl.kill();
        tl2.kill();
        tl3.kill();
      };
    }
  }, [screen, gsapEnd, gsapStart]);

  const allCards = useMemo(() => {
    return cards.map((card, i) => {
      return (
        <Card id={`card_${i}`} key={`card_${i}`}>
          <CardNumber>{zeroPad(i + 1)}</CardNumber>
          <Title>{card.title}</Title>
          <Text>{card.text}</Text>
          <CardBG id={`card_bg${i}`} bgPos={card.style}>
            <Inner id={`card_inner${i}`} />
          </CardBG>
        </Card>
      );
    });
  }, [cards]);

  return (
    <Wrapper>
      <H2>
        <HeadingAnimation text="$FCD Utility" topPerc={animationTopPerc} botPerc={animationBottomPerc} />
      </H2>
      <CardContainer>{allCards}</CardContainer>
    </Wrapper>
  );
};

export default Reveal;

const Wrapper = styled.section`
  background: ${colors.pureBlack};
  position: relative;

  height: 75.42vw;
  padding: 20.83vw 10vw 10.42vw 10vw;

  ${media.tablet} {
    height: 171.875vw;
    padding: 26.855vw 5.859vw 0 5.859vw;
  }

  ${media.mobile} {
    height: 436vw;
    padding: 0;
  }
`;

const H2 = styled.h3`
  ${text.desktop.h2}
  text-transform: uppercase;
  white-space: pre;

  margin-bottom: 2.78vw;

  ${media.tablet} {
    ${text.tablet.h2}
    position: relative;
    letter-spacing: -0.05em;
    left: -90px;
    width: 106.543vw;
    margin-bottom: 5.859vw;
  }

  ${media.mobile} {
    ${text.mobile.h1}
    width: 73.733vw;
    height: 26.667vw;
    margin: 0 auto;
    white-space: unset;
    text-align: center;
    margin-bottom: 9.333vw;
  }
`;

const CardContainer = styled.div`
  display: flex;
  justify-content: space-between;

  ${media.tablet} {
    display: flex;
    flex-direction: column;
    gap: 5.859vw;
  }

  ${media.mobile} {
    flex-direction: column;
    gap: 16vw;
  }
`;

const Title = styled.div`
  position: relative;
  z-index: 2;
  ${text.desktop.h5}
  color: ${colors.pureWhite};
  text-transform: uppercase;

  margin-bottom: 1.39vw;

  ${media.tablet} {
    ${text.tablet.h4}
    width: 670.359vw;
    margin-bottom: 1.758vw;
  }

  ${media.mobile} {
    ${text.mobile.h5}
    left: 8vw;
    top: 26.667vw;
  }
`;

const Text = styled.p`
  position: relative;
  z-index: 2;
  ${text.desktop.bodyS}
  color: ${colors.pureWhite};

  margin-bottom: 3.47vw;

  ${media.tablet} {
    ${text.tablet.bodyL}
    width: 68.359vw;
    margin-bottom: 0vw;
  }

  ${media.mobile} {
    ${text.mobile.bodyS}
    width: 70.4vw;
    left: 8vw;
    top: 26.667vw;
  }
`;

const Inner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: 0.4s;

  background: linear-gradient(180deg, #eeb93c 0%, #ff148199 100%);
  opacity: 0;
  ${media.mobile} {
    width: 91.733vw;
    height: 120vw;
  }
`;

const CardBG = styled.div<{ bgPos: string }>`
  overflow: hidden;
  position: absolute;

  width: 23.89vw;
  height: 31.25vw;
  top: 0;
  left: 0;

  &:before {
    position: absolute;
    ${(props) => props.bgPos};
    content: "";
    z-index: 0;
    transform-origin: center center;
    background-image: url(${BackgroundWEBP});
    background-size: cover;
    background-repeat: no-repeat;

    ${media.tablet} {
      width: 88.184vw;
      height: 37.109vw;
    }

    ${media.mobile} {
      width: 92vw;
      height: 120vw;

      :before {
        width: 91.733vw;
        height: 120vw;
      }
    }
  }

  ${media.tablet} {
    width: 88.184vw;
    height: 37.109vw;
  }

  ${media.mobile} {
    width: 91.733vw;
    height: 120vw;
  }
`;

const Card = styled.div`
  position: relative;
  border-radius: 1px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: 500ms;

  box-shadow: 0vw 0.69vw 1.39vw rgba(0, 0, 0, 0.5);

  ${media.hover} {
    :hover {
      transform: scale(1.15);

      ${Inner} {
        opacity: 1;
        transition: 0.4s;
      }
    }
  }

  width: 23.89vw;
  height: 31.25vw;
  padding: 3.47vw 2.78vw;

  ${media.tablet} {
    transition: 0ms;
    width: 88.184vw;
    height: 37.109vw;
    padding: 5.273vw 13.965vw 11.914vw 5.859vw;
  }

  ${media.mobile} {
    width: 91.733vw;
    height: 120vw;
    left: 4vw;
  }
`;

const CardNumber = styled.p`
  position: relative;
  z-index: 2;
  ${text.desktop.accent1}
  color: ${colors.pureWhite};

  margin-bottom: 5.56vw;

  ${media.tablet} {
    display: none;
  }

  ${media.mobile} {
    ${text.mobile.accent1}
    left: 8vw;
    top: 14.4vw;
    width: 11.733vw;
    height: 16.8vw;
  }
`;
